import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import SEO from "../layout/SEO";

/**
 * Écran d'état plein page (404, article introuvable, chargement…).
 * Mise en page "split + suggestions" : message éditorial à gauche,
 * liste de rebond à droite. Réutilisable pour toute page vide du front.
 *
 * Props :
 *  - seo : { title, description }
 *  - kicker : libellé court en capitales (ex. "Erreur 404")
 *  - title : node — le titre (peut contenir un <em> pour l'accent)
 *  - lead : texte d'accroche
 *  - primary : { to, label } — bouton principal
 *  - suggestionsTitle : titre de la colonne de droite
 *  - suggestions : [{ to, type, title, excerpt }]
 *  - loading : bool — affiche l'état de chargement (squelettes)
 *  - modeDark : passé au Header (def. true)
 */
const StatusScreen = ({
    seo = {},
    kicker,
    title,
    lead,
    primary,
    suggestionsTitle = "À lire à la place",
    suggestions = [],
    loading = false,
    modeDark = false
}) => {
    const hasSuggestions = loading || suggestions.length > 0;

    return (
        <>
            <SEO
                title={seo.title}
                description={seo.description || lead}
                keywords="article, 404, introuvable"
            />
            <Header modeDark={modeDark} />

            <main className="status-screen">
                <section className={`status-screen-inner ${hasSuggestions ? "" : "status-screen-inner--solo"}`}>
                    <div className="status-shell">
                        <div className={`status-grid ${hasSuggestions ? "" : "status-grid--solo"}`}>
                            <div className="status-lead">
                                {kicker && <div className="status-kicker">{kicker}</div>}
                                <h1 className="status-title">{title}</h1>
                                {loading ? (
                                    <div className="status-skeleton-lines" aria-hidden="true">
                                        <span /><span /><span className="is-short" />
                                    </div>
                                ) : (
                                    <p className="status-lead-text">{lead}</p>
                                )}
                                {primary && !loading && (
                                    <Link to={primary.to} className="status-btn">
                                        {primary.label}
                                        <ArrowRight size={18} />
                                    </Link>
                                )}
                            </div>

                            {hasSuggestions && (
                                <>
                                    <div className="status-divider" aria-hidden="true" />
                                    <div className="status-sugg-block">
                                        <div className="status-sugg-title">
                                            {loading ? "Chargement de l'article" : suggestionsTitle}
                                        </div>
                                        <div className="status-sugg-list">
                                            {loading
                                                ? [0, 1, 2].map((i) => (
                                                    <div className="status-sugg-item is-skeleton" key={i} aria-hidden="true">
                                                        <span className="sk-tag" />
                                                        <div className="sk-body">
                                                            <span className="sk-line" />
                                                            <span className="sk-line is-short" />
                                                        </div>
                                                    </div>
                                                ))
                                                : suggestions.map((item, index) => (
                                                    <Link to={item.to} className="status-sugg-item" key={item.to || index}>
                                                        {item.type && <span className="status-sugg-tag">{item.type}</span>}
                                                        <div className="status-sugg-info">
                                                            <strong>{item.title}</strong>
                                                            {item.excerpt && <span>{item.excerpt}</span>}
                                                        </div>
                                                        <ArrowRight size={17} className="status-sugg-arrow" />
                                                    </Link>
                                                ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </>
    );
};

export default StatusScreen;
